import React from 'react';
import { useCalculator } from './hooks/useCalculator';
import { Header } from './components/Layout/Header';
import { Footer } from './components/Layout/Footer';
import { PdfExportCard } from './components/Layout/PdfExportCard';
import { CalculatorInputs } from './components/Calculator/CalculatorInputs';
import { EmployeeValueOutputs } from './components/Calculator/EmployeeValueOutputs';
import { SustainabilityOutputs } from './components/Calculator/SustainabilityOutputs';
import { RealWorldImpactOutputs } from './components/Calculator/RealWorldImpactOutputs';
import { formatNumber, formatHours } from './utils/formatters';
import { Coffee, Clock, Calendar } from 'lucide-react';

const App: React.FC = () => {
  const {
    inputs,
    scaledOutputs,
    timePeriod,
    setInput,
    setTimePeriod,
  } = useCalculator();

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header />

      <main className="flex-1 py-8">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Calculator Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Inputs */}
            <div>
              <CalculatorInputs
                inputs={inputs}
                onInputChange={setInput}
                timePeriod={timePeriod}
                onTimePeriodChange={setTimePeriod}
              />
            </div>

            {/* Right Column - Outputs */}
            <div className="flex flex-col gap-6">

              {/* Summary Card */}
              <div className="bg-white rounded-2xl p-6 lg:py-8 shadow-lg">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-orange" />
                  <h3 className="text-lg font-serif font-semibold text-offblack">
                    {timePeriod === 'day' && 'Daily'}
                    {timePeriod === 'week' && 'Weekly'}
                    {timePeriod === 'month' && 'Monthly'}
                    {timePeriod === 'quarter' && 'Quarterly'}
                    {timePeriod === 'year' && 'Yearly'}
                    {' '}Coffee Summary
                  </h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl bg-[#FAF0EC] transition-transform duration-200 hover:scale-[1.02]">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-transparent">
                        <Coffee className="w-5 h-5 text-[#6F8D90]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#A2B7BB] mb-1">
                          {timePeriod === 'day' && 'Cups Served Today'}
                          {timePeriod === 'week' && 'Cups Served This Week'}
                          {timePeriod === 'month' && 'Cups Served This Month'}
                          {timePeriod === 'quarter' && 'Cups Served This Quarter'}
                          {timePeriod === 'year' && 'Cups Served This Year'}
                        </p>
                        <p className="text-2xl font-bold text-[#6F8D90] leading-tight">
                          {formatNumber(scaledOutputs.totalCupsPerDay, 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl bg-[#FAF0EC] transition-transform duration-200 hover:scale-[1.02]">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-transparent">
                        <Clock className="w-5 h-5 text-[#6F8D90]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#A2B7BB] mb-1">Total Break Time</p>
                        <p className="text-2xl font-bold text-[#6F8D90] leading-tight">
                          {formatHours(scaledOutputs.employeeValue.timeSavedHours)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Employee Value Outputs */}
              <div className="flex-1">
                <EmployeeValueOutputs data={scaledOutputs.employeeValue} />
              </div>

              {/* Sustainability Outputs */}
              <div className="flex-1">
                <SustainabilityOutputs data={scaledOutputs.sustainability} />
              </div>
            </div>
          </div>

          {/* Real World Impact - Full Width */}
          <div className="mt-8">
            <RealWorldImpactOutputs data={scaledOutputs.sustainability} />
          </div>

          {/* PDF Export Card */}
          <PdfExportCard
            scaledOutputs={scaledOutputs}
            timePeriod={timePeriod}
            inputs={inputs}
          />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default App;
